import { Stay } from 'guest-data-types-ts-lib/types/stay';
import { Access, Guests, HomeInfo, Reservation } from 'guest-data-types-ts-lib';
import { Unit } from 'guest-data-types-ts-lib/types/unit';
import { ExtraData, resStatus } from '../../types/webChat.type';
import moment, { Moment } from 'moment-timezone';
import segmentService from '../../services/segment/segmentService';
import ddLogger from '../../logging/ddLogger';
import _ from 'lodash';
import { UserState } from '../../types/user.store.type';
const getResStatus = (now: Moment, stay: Stay, isCancelled: boolean): { resStatus: resStatus; nights: number } => {
    const checkIn = moment(new Date(stay.check_in_date_time));
    const checkOut = moment(new Date(stay.check_out_date_time));
    const nights = Math.round(checkOut.diff(checkIn, 'hours') / 24);

    let resStatus: resStatus;

    if (isCancelled) {
        resStatus = 'cancelled';
    } else if (now.isAfter(checkOut)) {
        resStatus = 'complete';
    } else if (now.isBefore(checkIn)) {
        resStatus = 'upcoming';
    } else {
        resStatus = 'in_progress';
    }

    return { resStatus, nights };
};

export const getExtraData = (
    now: Moment,
    isCancelled: boolean,
    stay: Stay,
    reservation: Reservation,
    unit: Unit,
    guests: Guests,
    access: Access,
    homeInfo: HomeInfo,
    user: UserState
): ExtraData => {
    const { resStatus, nights } = getResStatus(now, stay, isCancelled);
    const { user: localUser } = user;

    return {
        guestName: `${guests.primary.first_name} ${guests.primary.last_name}`,
        guestEmail: localUser?.email || guests.primary.email,
        reservation: {
            adults: guests.current_counts.adults,
            children: guests.current_counts.children,
            id: reservation.id,
            nights,
            pets: guests.current_counts.pets,
            channelId: reservation.reservation_source_id,
            checkIn: stay.check_in_date,
            checkOut: stay.check_out_date,
            resStatus,
            wifiNetwork: access.is_masked ? access.wifi : access.wifi.wifi_network,
            wifiPassword: access.is_masked ? access.wifi : access.wifi.wifi_password,
            lateCheckout: stay.late_check_out_purchased,
        },
        unit: {
            address: {
                address1: unit.address_1,
                address2: unit.address_2,
                city: unit.city,
                country: unit.country,
                state: unit.state,
            },
            imageSrc: unit.photos.length > 0 ? unit.photos[0].url : '',
            alarmCode: access.alarm,
            checkInTime: stay.check_in_time,
            checkOutTime: stay.check_out_time,
            garageParking: !!unit.vehicle_info.garage_parking,
            gateCode: access.gate,
            gateCodeNotes: access.gate_notes,
            hasActiveElock: !!(access.elocks && access.elocks.length > 0),
            id: unit.id,
            maxAdults: unit.max_adults,
            maxChildren: unit.max_children,
            maxOccupancy: unit.max_occupancy,
            maxPets: unit.max_occupancy_pets,
            name: unit.name,
            parkingNotes: unit.vehicle_info.parking_instructions,
            parkingTotalCars: unit.vehicle_info.parking_total_cars,
            primaryLockbox: access.lockbox,
            title: unit.unit_title,
            beds: {
                child: homeInfo.child_bed,
                double: homeInfo.double_bed,
                king: homeInfo.king_bed,
                queen: homeInfo.queen_bed,
                twin: homeInfo.twin_bed,
            },
        },
    };
};

const getDateInString = (date: Moment): string => {
    return moment(date.format()).locale('en-US').format('h A');
};

interface scheduleType {
    isEnable: boolean;
    localTimeToOpen: string;
    localTimeToClose: string;
}

export const getChatSchedule = (now: Moment, timeZone: string): scheduleType => {
    const openTime = moment().tz(timeZone).startOf('day').add(4, 'hours');
    const closeTime = moment().tz(timeZone).startOf('day').add(23, 'hours');
    return {
        isEnable: now.isBetween(openTime.format(), closeTime.format()),
        localTimeToOpen: getDateInString(openTime),
        localTimeToClose: getDateInString(closeTime),
    };
};

export const tracking = (isEnable: boolean, action: string, location: any, reservationId: string) => {
    const chat_status = isEnable ? 'open' : 'close';
    ddLogger.info(`Web Chat - user ${action} for reservation ${reservationId}`, {
        web_chat: {
            chat_status,
            chat_action: action,
        },
    });
    segmentService.trackEvent('Trip Portal Chat', {
        action,
        chat_status,
        page: location.pathname,
    });
};
